
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useReportsData } from "@/hooks/reports";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { DashboardData, StatItem, RecentOrder, FactoryPerformanceData, RawOrderData } from "./types";
import { formatOrders, calculateFactoryPerformance, calculateStats } from "./dashboardUtils";

export const useDashboardData = (): DashboardData => {
  const [stats, setStats] = useState<StatItem[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [factories, setFactories] = useState<FactoryPerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  // Use subscription data
  const { monthlyRevenue: subscriptionRevenue, loading: subscriptionLoading, userSubscriptions } = useSubscriptions();
  
  // Use the existing reports hook to potentially leverage cached data
  const { orders: cachedOrders } = useReportsData();

  useEffect(() => {
    if (!subscriptionLoading) {
      fetchDashboardData();
    }
  }, [subscriptionLoading, subscriptionRevenue, userSubscriptions]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log("=== DASHBOARD DATA FETCH ===");
      console.log("Subscription revenue from hook:", subscriptionRevenue);
      console.log("User subscriptions:", userSubscriptions);
      
      // Get current date and date a month ago
      const currentDate = new Date();
      const lastMonthDate = new Date();
      lastMonthDate.setMonth(currentDate.getMonth() - 1);
      
      // Format dates for Supabase queries
      const currentDateStr = currentDate.toISOString();
      const lastMonthDateStr = lastMonthDate.toISOString();
      
      // Fetch limited recent orders with optimized query
      const { data: allOrders, error: ordersError } = await supabase
        .from("orders")
        .select("id, order_number, created_at, status, total_amount, factory_id,  customer_id, customers( customer_name)")
        .order("created_at", { ascending: false })
        .limit(50);
        
      if (ordersError) throw ordersError;
      
      // Use count query instead of fetching all orders for comparison
      const { count: lastMonthOrdersCount, error: lastMonthError } = await supabase
        .from("orders")
        .select("id", { count: 'exact', head: true })
        .gte("created_at", lastMonthDateStr)
        .lte("created_at", currentDateStr);
        
      if (lastMonthError) throw lastMonthError;
      
      // Get active factories only
      const { data: factoryData, error: factoryError } = await supabase
        .from("factories")
        .select("id, name")
        .eq("active", true);
        
      if (factoryError) throw factoryError;
      
      // Count new customers from this month instead of fetching all
      const { count: newCustomersCount, error: customerCountError } = await supabase
        .from("customers")
        .select("id", { count: 'exact', head: true })
        .gte("created_at", lastMonthDateStr)
        .lte("created_at", currentDateStr);
        
      if (customerCountError) throw customerCountError;
      
      // Calculate stats
      const totalOrders = allOrders.length;
      
      // Active factories
      const activeFactories = factoryData.length;
      
      // Calculate order revenue from completed orders
      const orderRevenue = allOrders
        .filter(order => order.status === "completed")
        .reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
      
      // Get previous month's order revenue using a separate query to optimize
      const { data: prevMonthCompletedOrders, error: prevMonthError } = await supabase
        .from("orders")
        .select("total_amount")
        .eq("status", "completed")
        .lt("created_at", lastMonthDateStr)
        .gte("created_at", new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() - 1).toISOString())
        .limit(100);
        
      if (prevMonthError) throw prevMonthError;
      
      const prevMonthOrderRevenue = prevMonthCompletedOrders
        ? prevMonthCompletedOrders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0)
        : 0;
      
      // Format orders for the recent orders table
      const formattedOrders = formatOrders(allOrders as RawOrderData[], factoryData);
      
      // Calculate factory performance
      const factoryPerformance = calculateFactoryPerformance(factoryData, allOrders as RawOrderData[]);
      
      // Calculate stats with subscription revenue
      console.log("=== DASHBOARD STATS CALCULATION ===");
      console.log("Calculating stats with subscription revenue:", subscriptionRevenue);
      console.log("Active subscriptions count:", userSubscriptions.filter(sub => 
        sub.status === 'active' && sub.plan_price > 0
      ).length);
      
      const calculatedStats = calculateStats(
        totalOrders,
        activeFactories,
        newCustomersCount || 0,
        orderRevenue,
        prevMonthOrderRevenue,
        lastMonthOrdersCount || 0,
        subscriptionRevenue,
        0 // Previous month subscription revenue - would need additional query
      );
      
      console.log("=== FINAL DASHBOARD STATS ===");
      console.log("Calculated stats:", calculatedStats);
      
      // Update state with the calculated data
      setStats(calculatedStats);
      setRecentOrders(formattedOrders);
      setFactories(factoryPerformance);
      setLoading(false);
      
      console.log("Dashboard data fetched successfully");
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error.message);
      
      // Try to use cached data from reports if available
      if (cachedOrders?.length > 0) {
        console.log("Falling back to cached data from reports");
        
        // Calculate basic stats from cached data
        const totalOrders = cachedOrders.length;
        const activeFactories = 0;
        
        // Set fallback stats using cached data
        setStats([
          {
            title: "Total Orders",
            value: totalOrders.toString(),
            icon: "shopping-cart-2-line",
            change: "Based on cached data",
            trend: "neutral" as const,
          },
          {
            title: "Active Factories",
            value: activeFactories.toString(),
            icon: "building-2-line",
            change: "Based on cached data",
            trend: "neutral" as const,
          },
          {
            title: "New Customers",
            value: "N/A",
            icon: "user-line",
            change: "Data unavailable",
            trend: "neutral" as const,
          },
          {
            title: "Order Revenue",
            value: `$${cachedOrders
              .filter(order => order.status === "completed")
              .reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0).toFixed(2)}`,
            icon: "money-dollar-circle-line",
            change: "Based on cached data",
            trend: "neutral" as const,
          },
          ...(subscriptionRevenue > 0 ? [{
            title: "Subscription Revenue",
            value: `$${subscriptionRevenue.toFixed(2)}`,
            icon: "repeat-line",
            change: "Monthly recurring revenue",
            trend: "neutral" as const,
          }] : [])
        ]);
      }
      
      toast({
        title: "Error loading dashboard",
        description: error.message.includes("statement timeout") 
          ? "Database query timed out. Showing partial data." 
          : "Could not load dashboard data. Please try again later.",
        variant: "destructive",
      });
      
      setLoading(false);
    }
  };

  return {
    stats,
    recentOrders,
    factories,
    loading: loading || subscriptionLoading,
    refreshData: fetchDashboardData
  };
};

// Re-export types from the types file for easier imports
export * from "./types";
